import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonWithLoading, Label, AlertBar, FilterDropdownContainer, DatePicker, Button, Input } from 'scorer-ui-kit';
import { BoxContainer } from '../components/BoxContainer';
import { CloseIcon, FieldContainer, overlayStyle } from '../Style';
import { downloadLogs } from '../apis';
import styled from 'styled-components';
import Popup from 'reactjs-popup';

const AlertContainer = styled.div`
  width: 500px;
`;

const PopupContainer = styled.div`
  width: max-content;
  padding: 30px;
`;

const InputField = styled(Input)`
  width: 150px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 0 10px;
  justify-content: flex-end;
`;

interface IDate {
  fromDate: Date,
  toDate: Date
}

function LogDownloadTab() {
  const [dateRange, setDateRange] = useState<IDate>({ fromDate: new Date(), toDate: new Date() });
  const [alert, setAlert] = useState<{ key: any, message: string, type: 'success' | 'error' | 'warning' | 'info' | undefined }>({ key: new Date(), message: '', type: undefined });
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { t } = useTranslation(['AuditLogs', 'Common']);

  function isValidDate(date: Date) {
    var regEx = /^\d{4}-\d{2}-\d{2}$/;
    if (regEx.exec(date.toString())) return false;
    return true;
  }

  function formatDate(date: any) {
    var d = new Date(date),
      month = '' + (d.getMonth() + 1),
      day = '' + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2)
      month = '0' + month;
    if (day.length < 2)
      day = '0' + day;

    return [year, month, day].join('-');
  }

  useEffect(()=>{
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    setDateRange({fromDate: startDate, toDate: new Date()})
  },[]);

  const onDateChange = useCallback((date) => {
    setDateRange({ fromDate: date.start, toDate: date.end });
  }, []);

  const onDownload = () => {
    setModalOpen(false);
    setLoading(true);
    setAlert({ key: new Date(), message: '', type: undefined });
    if (!isValidDate(dateRange.fromDate || '') || dateRange.fromDate === null) {
      setAlert({ key: new Date(), message: t('logDownloadTab.fromDateFieldIsRequired'), type: 'error' });
      setLoading(false);
      return;
    }
    if (!isValidDate(dateRange.toDate || '') || dateRange.toDate === null) {
      setAlert({ key: new Date(), message: t('logDownloadTab.toDateFieldIsRequired'), type: 'error' });
      setLoading(false);
      return;
    }

    if (dateRange.fromDate > dateRange.toDate) {
      setAlert({ key: new Date(), message: t('logDownloadTab.fromDateShoulBeLessThanToDate'), type: 'error' });
      setLoading(false);
      return;
    }
    if (formatDate(dateRange.toDate) !== formatDate(new Date()) && dateRange.toDate > new Date()) {
      setAlert({ key: new Date(), message: t('logDownloadTab.toDateShoulNotBeGreaterThanToday'), type: 'error' });
      setLoading(false);
      return;
    }

    downloadLogs(formatDate(dateRange.fromDate) || '', formatDate(dateRange.toDate) || '')
      .then((res) => {
        setLoading(false);
        if(res.data.status_code === '5'){
          setAlert({ key: new Date(), message: t('logDownloadTab.auditLogIsNotAvailable'), type: 'error' });
        } else {
          let name = decodeURIComponent(res.headers['content-disposition']);
          name = name.substring(
            name.lastIndexOf(';'),
            name.lastIndexOf('filename=')
          ).split('filename=')[1];
  
          let fileName = '';
          if (name.length > 0) {
            fileName = name;
          } else {
            fileName = 'LEDMonitor_export_Settings_template.csv';
          }
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
        }
      })
      .catch((err) => {
        if (err.response.data.message) {
          setAlert({ key: new Date(), message: err.response.data.message, type: 'error' });
        }
        console.error(err);
      });
  };

  const onChangeDateClick = () => {
    setModalOpen(true);
    setAlert({ key: new Date(), message: '', type: undefined });
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      <>
        <Popup
          modal
          open={modalOpen}
          onClose={closeModal}
          {...{ overlayStyle }}
          closeOnDocumentClick
        >
          <PopupContainer>
            <CloseIcon onClick={()=>setModalOpen(false)}>x</CloseIcon>
            <FilterDropdownContainer style={{ boxShadow: 'none' }}>
              <DatePicker
                dateMode='interval'
                dateTimeTextUpper={t('logDownloadTab.fromDate')}
                dateTimeTextLower={t('logDownloadTab.toDate')}
                timeMode='off'
                initialValue={{start:dateRange.fromDate,end:dateRange.toDate}}
                updateCallback={onDateChange}
              />
            </FilterDropdownContainer>
          </PopupContainer>
        </Popup>
        <BoxContainer title=''>
          <FieldContainer>
            <Label htmlFor='name' labelText={t('logDownloadTab.fromDate')} />
            <InputField name='name' placeholder={t('logDownloadTab.fromDate')} value={formatDate(dateRange.fromDate)} readOnly />
          </FieldContainer>
          <FieldContainer>
            <Label htmlFor='note1' labelText={t('logDownloadTab.toDate')} />
            <InputField name='note' placeholder={t('logDownloadTab.toDate')} value={formatDate(dateRange.toDate)} readOnly />
          </FieldContainer>

          <ButtonsContainer>
            <Button design='primary' onClick={onChangeDateClick}>{t('changeDate')}</Button>
            <ButtonWithLoading loading={loading} design='primary' onClick={onDownload}>{t('download')}</ButtonWithLoading>
          </ButtonsContainer>
        </BoxContainer>
      </>
      {alert.message !== '' && (
        <AlertContainer key={alert.key}>
          <AlertBar type={alert.type} message={alert.message} />
        </AlertContainer>
      )}
    </>
  );
}

export default LogDownloadTab;
